/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, AlertCircle, Send, CheckCircle, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { getExerciseIcon } from "../sharedUtils";

export default function TrainingPreviewPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToast } = useToaster();

    const [previewData, setPreviewData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);

    // Submissions
    const [adminAnswers, setAdminAnswers] = useState<Record<number, string>>({});
    const [adminResults, setAdminResults] = useState<Record<number, any>>({});
    const [submittingId, setSubmittingId] = useState<number | null>(null);
    const submitInFlightRef = useRef(false);
    const activeTrainingIdRef = useRef(id);
    activeTrainingIdRef.current = id;

    useEffect(() => {
        if (!id) return;
        const fetchPreview = async () => {
            setLoading(true);
            setAdminAnswers({});
            setAdminResults({});
            try {
                const res = await apiService.get(`api/gp/exercises/${id}`);
                const fetchedData = res.data || [];
                setPreviewData(fetchedData);

                // --- NEW CODE: Pre-populate answers and results for attempted exercises ---
                const initialAnswers: Record<number, string> = {};
                const initialResults: Record<number, any> = {};

                fetchedData.forEach((lesson: any) => {
                    if (lesson.exercises && Array.isArray(lesson.exercises)) {
                        lesson.exercises.forEach((ex: any) => {
                            if (ex.attempted) {
                                // Pre-fill the selected option/text
                                initialAnswers[ex.id] = ex.userAnswer || "";

                                // Pre-fill the feedback and correctness box
                                initialResults[ex.id] = {
                                    correct: ex.correct,
                                    score: ex.score,
                                    feedback: ex.feedback,
                                    correctAnswer: ex.correctAnswer || ""
                                };
                            }
                        });
                    }
                });

                setAdminAnswers(initialAnswers);
                setAdminResults(initialResults);
                // --------------------------------------------------------------------------

            } catch (error: any) {
                addToast(error?.response?.data?.error || "Failed to load preview.", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchPreview();
    }, [id, addToast]);

    const handleSubmit = async (exerciseId: number) => {
        const answer = adminAnswers[exerciseId];
        if (!answer?.trim() || submitInFlightRef.current || adminResults[exerciseId]) return;
        submitInFlightRef.current = true;
        const trainingIdAtSubmit = id;
        try {
            setSubmittingId(exerciseId);
            const res = await apiService.post("api/gp/exercises/submit", { exerciseId, userAnswer: answer });
            if (activeTrainingIdRef.current !== trainingIdAtSubmit) return;
            setAdminAnswers(prev => ({ ...prev, [exerciseId]: answer }));
            setAdminResults(prev => ({ ...prev, [exerciseId]: res.data }));
            addToast("Answer submitted.", "success");
        } catch (error: any) {
            if (activeTrainingIdRef.current !== trainingIdAtSubmit) return;
            addToast(error?.response?.data?.error || "Failed to submit answer.", "error");
        } finally {
            submitInFlightRef.current = false;
            setSubmittingId(null);
        }
    };

    const currentLesson = previewData[currentLessonIndex];

    return (
        <div className="flex flex-col h-full min-h-[calc(100vh-100px)] bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden transition-colors duration-300 border border-gray-200 dark:border-gray-800">

            {/* HEADER */}
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 shrink-0 flex items-center gap-4 sticky top-0 z-20 shadow-sm">
                <button onClick={() => navigate("/admin/training")} className="p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Back to Trainings">
                    <ArrowLeft size={20} />
                </button>
                <div className="flex items-center gap-2">
                    <Eye className="text-indigo-600 dark:text-indigo-400" size={24} />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Training Preview</h2>
                </div>
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto p-6 lg:p-8">
                <div className="max-w-4xl mx-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <RefreshCw className="animate-spin text-indigo-500 mb-4" size={32} />
                            <p className="text-gray-500 dark:text-gray-400 font-medium">Loading exercises...</p>
                        </div>
                    ) : previewData.length === 0 ? (
                        <div className="text-center py-20 text-gray-500 dark:text-gray-400 flex flex-col items-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                            <AlertCircle size={48} className="mb-4 text-yellow-500" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Exercises Found</h3>
                            <p>This training might not have generated exercises yet.</p>
                        </div>
                    ) : currentLesson ? (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm flex flex-col">

                            {/* LESSON CONTENT */}
                            <div className="px-8 py-6 bg-indigo-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                                <div>
                                    <h4 className="font-bold text-gray-900 dark:text-white text-xl">Lesson {currentLesson.lessonNumber}: {currentLesson.lessonTitle}</h4>
                                    <p className="text-sm text-indigo-700 dark:text-indigo-400 font-medium mt-1">Module {currentLessonIndex + 1} of {previewData.length}</p>
                                </div>
                                <span className="text-sm font-semibold text-indigo-700 bg-white dark:bg-gray-700 dark:text-indigo-300 px-4 py-2 rounded-lg shadow-sm border border-indigo-100 dark:border-gray-600">
                                    {currentLesson.exercises.length} Questions
                                </span>
                            </div>

                            {/* LESSON QUESTIONS */}
                            <div className="p-8 divide-y divide-gray-100 dark:divide-gray-700/50">
                                {currentLesson.exercises.map((ex: any, idx: number) => {
                                    const isOptions = ex.options && ex.options.length > 0;
                                    const currentValue = adminAnswers[ex.id] || "";
                                    const result = adminResults[ex.id];
                                    const isAnswered = Boolean(result);
                                    const isInputLocked = isAnswered || submittingId === ex.id;

                                    return (
                                        <div key={ex.id} className={`${idx !== 0 ? 'pt-10 mt-10' : ''}`}>
                                            <div className="flex items-start gap-4">
                                                <div className="mt-1 bg-gray-50 dark:bg-gray-700 p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm text-indigo-600 dark:text-indigo-400">
                                                    {getExerciseIcon(ex.type)}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-5">
                                                        {idx + 1}. {ex.question} <span className="text-red-500">*</span>
                                                    </p>

                                                    <div className="mb-5">
                                                        {isOptions ? (
                                                            <div className="grid gap-3">
                                                                {ex.options.map((opt: string, i: number) => (
                                                                    <label key={i} className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${isInputLocked ? 'cursor-not-allowed' : 'cursor-pointer'} ${currentValue === opt ? 'bg-indigo-50 border-indigo-300 dark:bg-indigo-900/30 dark:border-indigo-700 shadow-sm' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                                                                        <input type="radio" value={opt} checked={currentValue === opt} disabled={isInputLocked} onChange={(e) => setAdminAnswers(prev => ({ ...prev, [ex.id]: e.target.value }))} className="w-5 h-5 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50" />
                                                                        <span className="text-base font-medium text-gray-700 dark:text-gray-300">{opt}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <textarea rows={4} value={currentValue} disabled={isInputLocked} onChange={(e) => setAdminAnswers(prev => ({ ...prev, [ex.id]: e.target.value }))} placeholder="Type your detailed answer here..." className="w-full p-4 text-base rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 focus:bg-white dark:focus:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed" />
                                                        )}
                                                    </div>

                                                    <button onClick={() => handleSubmit(ex.id)} disabled={!currentValue || isAnswered || submittingId !== null} className="flex items-center gap-2 px-6 py-3 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm">
                                                        {submittingId === ex.id ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send size={16} />} Submit Answer
                                                    </button>

                                                    {result && (
                                                        <div className={`mt-6 p-5 rounded-xl border animate-in fade-in slide-in-from-top-2 ${result.correct ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-900/30' : 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-900/30'}`}>
                                                            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/5 dark:border-white/10">
                                                                {result.correct ? <CheckCircle className="text-green-600 dark:text-green-400" size={20} /> : <AlertCircle className="text-red-600 dark:text-red-400" size={20} />}
                                                                <span className={`text-base font-bold ${result.correct ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                                                                    {result.correct ? 'Correct' : 'Incorrect'} <span className="opacity-75 font-medium ml-1">(Score: {result.score})</span>
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-800 dark:text-gray-200 mb-3 leading-relaxed"><strong>AI Feedback:</strong> {result.feedback}</p>
                                                            <div className="p-3 bg-white/60 dark:bg-gray-900/40 rounded border border-gray-200 dark:border-gray-700/50">
                                                                <p className="text-sm text-gray-800 dark:text-gray-200"><strong>Correct Answer:</strong> {result.correctAnswer}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* NAVIGATION FOOTER */}
                            <div className="px-8 py-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
                                <button
                                    onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setCurrentLessonIndex(prev => prev - 1); }}
                                    disabled={currentLessonIndex === 0}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                                >
                                    <ChevronLeft size={16} /> Previous Lesson
                                </button>
                                <div className="hidden sm:block text-sm font-medium text-gray-500 dark:text-gray-400">
                                    {currentLessonIndex + 1} of {previewData.length}
                                </div>
                                <button
                                    onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setCurrentLessonIndex(prev => prev + 1); }}
                                    disabled={currentLessonIndex === previewData.length - 1}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                                >
                                    Next Lesson <ChevronRight size={16} />
                                </button>
                            </div>

                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}